import type { ReactNode } from 'react';

import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface StatCardProps {
  label: ReactNode;
  /** The number itself; already formatted. */
  value: ReactNode;
  /** One short line under the number: a delta, a breakdown, a hint. */
  detail?: ReactNode;
  /** Colours the detail line. */
  tone?: 'neutral' | 'positive' | 'warning' | 'primary';
  isLoading?: boolean;
  className?: string;
  /** `data-tour` value when a guided tour points at this tile. */
  tour?: string;
}

const TONE: Record<NonNullable<StatCardProps['tone']>, string> = {
  neutral: 'text-muted-foreground',
  positive: 'text-success',
  warning: 'text-warning-foreground dark:text-warning',
  primary: 'text-primary',
};

/** KPI tile: small label, big tabular number, one line of context. */
export function StatCard({
  label,
  value,
  detail,
  tone = 'neutral',
  isLoading = false,
  className,
  tour,
}: StatCardProps) {
  return (
    <div
      data-tour={tour}
      className={cn(
        'bg-card text-card-foreground flex flex-col gap-2 rounded-xl border px-5 py-4',
        className,
      )}
    >
      <span className="text-muted-foreground text-[13px]">{label}</span>
      {isLoading ? (
        <Skeleton className="h-8 w-16" />
      ) : (
        <span className="text-[28px] leading-none font-semibold tracking-tight tabular-nums">
          {value}
        </span>
      )}
      {detail !== undefined ? (
        <span className={cn('text-xs', TONE[tone])}>{isLoading ? ' ' : detail}</span>
      ) : null}
    </div>
  );
}
